import React, { useState, useEffect } from 'react';
import {Flex, Text, Blockquote, Title, Divider, Stack, ThemeIcon, Paper, Anchor} from '@mantine/core';
import {IconCircleCheck, IconInfoCircle, IconSeeding} from '@tabler/icons-react';
import { PersonCard } from './PersonCard';

import { PersonSkeleton} from './PersonSkeleton';
import {PersonBetter} from '../../types/Person';
import {fetchPeople, updatePerson} from "../../api/People";
import { Link } from 'react-router-dom';
import { notifications } from '@mantine/notifications';

const API_BASE_URL = process.env.REACT_APP_PATH_TO_API;

export function FamilyTree() {
    const [isLoading, setIsLoading] = useState(true);
    const [familyMembers, setFamilyMembers] = useState<PersonBetter[]>([]);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);


    useEffect(() => {
        loadFamilyMembers();
    }, []);

    const loadFamilyMembers = async () => {
        try {
            let familyMembers: PersonBetter[] = await fetchPeople();
            // console.log(`API BaseUrl: ${API_BASE_URL}`)
            // const response = await axios.get(`${API_BASE_URL}/person`, {
            //     params: {
            //         sub: localStorage.getItem('sub')
            //     },
            //     headers: {
            //         'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
            //         'Content-Type': 'application/json'
            //     }
            // });
            //
            // // Parse dates in the response data
            // let familyMembers: PersonBetter[] = response.data.map((member: PersonBetter) => ({
            //     ...member,
            //     dob: member.dob ? new Date(`${member.dob}`) : null
            // }));
            //
            // console.log('Parsed family members with dates:', familyMembers);
            setFamilyMembers(familyMembers);
        } catch (error) {
            console.error('Error fetching family members:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onUpdate = async (updatedMember: PersonBetter) => {
        setUpdateError(null);
        try {
            // Call the updatePerson API function to save changes
            
            // Use the updatePerson function from the API client instead of making a direct axios call
            // This ensures that relationships and life events are properly formatted and sent to the API
            const updatedPerson = await updatePerson(updatedMember);
            
            // Update the local state with the updated member, but preserve existing fields
            setFamilyMembers(prevMembers => {
                return prevMembers.map(member => {
                    if (member.seed_key === updatedMember.seed_key) {
                        // Merge the existing member with the updated data
                        // This preserves fields like profile_url that might not be returned by the API
                        const mergedMember = {
                            ...member,  // Keep all existing fields
                            ...updatedPerson,  // Override with updated fields
                            // Explicitly preserve profile_url if it exists in the original but not in the update
                            profile_url: updatedPerson.profile_url || member.profile_url
                        };
                        return mergedMember;
                    }
                    return member;
                });
            });
        } catch (error) {
            console.error('Error updating family person:', error);
            setUpdateError(error instanceof Error ? error.message : 'Failed to update family person');
            await loadFamilyMembers();
        }
    };
    
    const onRemovePerson = async (personToRemove: PersonBetter) => {
        setIsProcessing(true);
        setUpdateError(null);
        try {
            // This would be an API call in a real implementation
            console.log('Removing person:', personToRemove);
            
            // For now, just remove from local state
            setFamilyMembers(prevMembers => {
                return prevMembers.filter(member => member.seed_key !== personToRemove.seed_key);
            });
            
            notifications.show({
                title: 'Person Removed',
                message: `${personToRemove.first} ${personToRemove.last} has been removed from your family tree.`,
                color: 'green'
            });
        } catch (error) {
            console.error('Error removing family person:', error);
            setUpdateError(error instanceof Error ? error.message : 'Failed to remove family person');
            await loadFamilyMembers();
        } finally {
            setIsProcessing(false);
        }
    };
    
    const onMergePeople = async (sourcePerson: PersonBetter, targetPerson: PersonBetter, mergeOptions: any) => {
        setIsProcessing(true);
        setUpdateError(null);
        try {
            // Log the merge request for now
            console.log('Merge request:', {
                sourcePerson,
                targetPerson,
                mergeOptions
            });
            
            // This would be an API call in a real implementation
            // For now, just update the target person with merged data and remove the source person
            const mergedPerson: PersonBetter = {
                ...targetPerson,
                first: mergeOptions.firstName,
                middle: mergeOptions.middleName,
                last: mergeOptions.lastName,
                dob: mergeOptions.dob,
                profile_url: mergeOptions.profileImage,
                notes: mergeOptions.notes,
                // Use the selected relationships from merge options
                relationships: mergeOptions.relationships || [],
                // Use the selected life events from merge options
                life_events: mergeOptions.lifeEvents || []
            };
            
            // Remove source person and update target person
            setFamilyMembers(prevMembers => {
                return prevMembers
                    .filter(member => member.seed_key !== sourcePerson.seed_key)
                    .map(member => {
                        if (member.seed_key === targetPerson.seed_key) {
                            return mergedPerson;
                        }
                        return member;
                    });
            });
            
            notifications.show({
                title: 'People Merged',
                message: `${sourcePerson.first} ${sourcePerson.last} has been merged with ${targetPerson.first} ${targetPerson.last}.`,
                color: 'green'
            });
        } catch (error) {
            console.error('Error merging family people:', error);
            setUpdateError(error instanceof Error ? error.message : 'Failed to merge family people');
            await loadFamilyMembers();
        } finally {
            setIsProcessing(false);
        }
    };



    return (
        <Flex direction="column">
            <Title order={1}>Identify Faces</Title>
            <Divider mt="xs" />

            {updateError && (
                <Text color="red" mt="xs" mb="xs">
                    {updateError}
                </Text>
            )}
            

            <Blockquote color="blue" icon={<IconInfoCircle />} mt="lg" mb="lg">
                <p>
                    Your family tree isn't just a record of the past—it's the key to organizing and preserving your memories. By adding family members and their birthdays, you enable Sort My Shoebox's AI to more accurately predict when your photos were taken, helping you better organize your photo collection.
                </p>
                <p>
                    In the future, this space will also serve as a hub for tracking and storing genealogical information, such as marriages, children, occupations, and significant life events. With AI-powered suggestions, we'll help uncover the hidden clues in each photograph as you piece together your family history.
                </p>
            </Blockquote>
            <Flex
                wrap="wrap"
                direction={{ base: 'column', sm: 'row' }}
                gap={{ base: '10px', sm: '10px' }}
                justify="flex-start"
            >
                {isLoading
                    ? Array(4).fill(null).map((_, index) => <PersonSkeleton key={`skeleton-${index}`} />)
                    : familyMembers.map((person) => (
                        <PersonCard
                            key={person.seed_key}
                            person={person}
                            onUpdate={onUpdate}
                            onRemove={onRemovePerson}
                            onMerge={onMergePeople}
                            familyMembers={familyMembers}
                        />
                    ))}
                {familyMembers.length === 0 && !isLoading && (
                    <Paper w="100%" mt="md" shadow="md" radius="md" withBorder p="xl">
                        <Stack align="center" gap="md">
                            <ThemeIcon size="xl" radius="xl" color="blue">
                                <IconSeeding size={32} />
                            </ThemeIcon>
                            <Text size="lg" w={600} ta="center">
                                Looks like you haven't added any family members yet.
                            </Text>
                            <Text c="gray.6" ta="center">
                                Tag some people in the <Anchor component={Link} to="/identify-faces" underline="always">Identify Faces</Anchor> tab & they will show up here.
                            </Text>
                        </Stack>
                    </Paper>
                )}
            </Flex>
        </Flex>
    );
}